import { useEffect, useMemo, useRef, useState } from 'react';

import {
  inspectRomFile,
  type RomInspectionResult,
} from '../../../packages/rom-inspector/src/index';
import { adminAppMeta } from './app-meta';
import './styles.css';

type Platform = 'gb' | 'gbc' | 'gba' | 'nds';
type GameType = 'OFFICIAL' | 'ROM_HACK' | 'HOMEBREW' | 'OTHER';

interface MetadataCandidate {
  provider: string;
  externalId: string;
  title: string;
  summary: string | null;
  platform: Platform | null;
  releaseDate: string | null;
  developer: string | null;
  publisher: string | null;
  coverUrl: string | null;
}

interface GameRecord {
  id: string;
  title: string;
  slug: string;
  summary: string | null;
  gameType: GameType;
  platform: Platform;
  status: string;
  visibility: string;
  releaseDate: string | null;
  developer: string | null;
  publisher: string | null;
  externalMetadataSource: string | null;
  externalMetadataId: string | null;
  rowVersion: number;
}

interface VariantRecord {
  id: string;
  status: string;
  sha256: string;
  sizeBytes: number;
  runtimeProfileId?: string;
  objectKey?: string | null;
}

interface GameDetail extends GameRecord {
  variants: VariantRecord[];
}

interface UploadAuthorization {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresAt: string;
}

interface UploadRecord {
  id: string;
  variantId: string;
  status: string;
  expiresAt: string;
}

interface TestSessionRecord {
  id: string;
  variantId: string;
  runtimeProfileId: string;
  runtimeId: string;
  status: 'ACTIVE' | 'COMPLETED' | 'EXPIRED';
  expiresAt: string;
}

interface CatalogState {
  revision: number;
  updatedAt: string;
}

interface ApiErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
}

const localApiBase = 'http://127.0.0.1:8787';
const localPlayerBase = 'http://127.0.0.1:5174';
const configuredApiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(
  /\/$/,
  '',
);
const configuredPlayerBase = (import.meta.env.VITE_PLAYER_BASE_URL as string | undefined)?.replace(
  /\/$/,
  '',
);

function initialApiBase(): string {
  return configuredApiBase || localApiBase;
}

function playerBase(): string {
  return configuredPlayerBase || localPlayerBase;
}

function emptyForm() {
  return {
    title: '',
    slug: '',
    platform: 'gba' as Platform,
    gameType: 'OTHER' as GameType,
    summary: '',
    releaseDate: '',
    developer: '',
    publisher: '',
    externalMetadataSource: '',
    externalMetadataId: '',
  };
}

function formFromGame(game: GameRecord) {
  return {
    title: game.title,
    slug: game.slug,
    platform: game.platform,
    gameType: game.gameType,
    summary: game.summary ?? '',
    releaseDate: game.releaseDate ?? '',
    developer: game.developer ?? '',
    publisher: game.publisher ?? '',
    externalMetadataSource: game.externalMetadataSource ?? '',
    externalMetadataId: game.externalMetadataId ?? '',
  };
}

function inspectionPayload(inspection: RomInspectionResult) {
  return {
    fileName: inspection.name,
    sizeBytes: inspection.sizeBytes,
    extension: inspection.extension,
    sha256: inspection.sha256,
    platform: inspection.platform,
    internalTitle: inspection.internalTitle,
    gameCode: inspection.gameCode,
    revision: inspection.revision,
    region: null,
  };
}

async function parseApiFailure(response: Response): Promise<Error> {
  try {
    const payload = (await response.json()) as ApiErrorEnvelope;
    const code = payload.error?.code ?? `HTTP_${response.status}`;
    const message = payload.error?.message ?? response.statusText;
    return new Error(`${code}: ${message}`);
  } catch {
    return new Error(`HTTP_${response.status}: ${response.statusText}`);
  }
}

function uploadWithProgress(
  authorization: UploadAuthorization,
  file: File,
  onProgress: (percent: number) => void,
): { promise: Promise<void>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<void>((resolve, reject) => {
    xhr.open(authorization.method, authorization.url, true);
    for (const [name, value] of Object.entries(authorization.headers)) {
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`R2_UPLOAD_FAILED: HTTP ${xhr.status}`));
    });
    xhr.addEventListener('error', () => reject(new Error('R2_UPLOAD_NETWORK_ERROR')));
    xhr.addEventListener('abort', () => reject(new Error('R2_UPLOAD_ABORTED')));
    xhr.send(file);
  });
  return { promise, abort: () => xhr.abort() };
}

function preferredVariant(variants: VariantRecord[]): VariantRecord | null {
  const rank = ['PUBLISHED', 'READY', 'TESTING', 'UPLOADED', 'DRAFT', 'UPLOAD_FAILED'];
  for (const status of rank) {
    const match = variants.find((candidate) => candidate.status === status);
    if (match) return match;
  }
  return variants[0] ?? null;
}

export function App() {
  const [apiBase, setApiBase] = useState(initialApiBase);
  const [token, setToken] = useState(
    () => sessionStorage.getItem('retro-arcade-admin-token') ?? '',
  );
  const [status, setStatus] = useState(
    `${adminAppMeta.milestone} ready — authenticate to manage catalog metadata and publication.`,
  );
  const [busy, setBusy] = useState(false);
  const [games, setGames] = useState<GameRecord[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [createdGame, setCreatedGame] = useState<GameDetail | null>(null);
  const [query, setQuery] = useState('');
  const [metadataResults, setMetadataResults] = useState<MetadataCandidate[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [inspection, setInspection] = useState<RomInspectionResult | null>(null);
  const [duplicate, setDuplicate] = useState<VariantRecord | null>(null);
  const [variant, setVariant] = useState<VariantRecord | null>(null);
  const [upload, setUpload] = useState<UploadRecord | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [testSession, setTestSession] = useState<TestSessionRecord | null>(null);
  const [testToken, setTestToken] = useState<string | null>(null);
  const [compatibilityNotes, setCompatibilityNotes] = useState('');
  const [catalogState, setCatalogState] = useState<CatalogState | null>(null);
  const uploadAbortRef = useRef<(() => void) | null>(null);

  const authenticated = token.trim().length > 0;
  const canCreateGame = authenticated && Boolean(form.title.trim()) && !createdGame;
  const canSaveMetadata = Boolean(
    authenticated && createdGame && form.title.trim() && form.slug.trim(),
  );
  const canCreateVariant = Boolean(createdGame && inspection && !duplicate && !variant);
  const canUpload = Boolean(
    variant &&
    file &&
    inspection &&
    inspection.sha256 === variant.sha256 &&
    inspection.sizeBytes === variant.sizeBytes &&
    !upload &&
    (variant.status === 'DRAFT' || variant.status === 'UPLOAD_FAILED'),
  );
  const canCreateTestSession = variant?.status === 'UPLOADED' && !testSession;
  const canRecordCompatibility = variant?.status === 'TESTING' && testSession?.status === 'ACTIVE';
  const canPublish = createdGame?.status === 'READY' && variant?.status === 'READY';
  const canUnpublish = createdGame?.status === 'PUBLISHED' && variant?.status === 'PUBLISHED';

  const api = useMemo(
    () =>
      async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
        const headers = new Headers(init.headers);
        headers.set('authorization', `Bearer ${token.trim()}`);
        if (init.body && !headers.has('content-type')) {
          headers.set('content-type', 'application/json');
        }
        const response = await fetch(`${apiBase}${path}`, { ...init, headers });
        if (!response.ok) throw await parseApiFailure(response);
        return (await response.json()) as T;
      },
    [apiBase, token],
  );

  async function refreshCatalogState() {
    if (!authenticated) return;
    try {
      const payload = await api<{ catalogState: CatalogState }>('/api/v1/admin/catalog-state');
      setCatalogState(payload.catalogState);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Unable to load catalog revision.');
    }
  }

  async function refreshGames() {
    if (!authenticated) return;
    try {
      const payload = await api<{ games: GameRecord[] }>('/api/v1/admin/games');
      setGames(payload.games);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Unable to load games.');
    }
  }

  async function refreshSelectedGame(gameId: string, message?: string) {
    const payload = await api<{ game: GameDetail }>(`/api/v1/admin/games/${gameId}`);
    setCreatedGame(payload.game);
    setVariant(preferredVariant(payload.game.variants));
    setForm(formFromGame(payload.game));
    if (message) setStatus(message);
  }

  async function selectExistingGame(gameId: string) {
    setBusy(true);
    setStatus('Loading owner catalog record…');
    try {
      const payload = await api<{ game: GameDetail }>(`/api/v1/admin/games/${gameId}`);
      setCreatedGame(payload.game);
      setVariant(preferredVariant(payload.game.variants));
      setForm(formFromGame(payload.game));
      setFile(null);
      setInspection(null);
      setDuplicate(null);
      setUpload(null);
      setUploadProgress(0);
      setTestSession(null);
      setTestToken(null);
      setCompatibilityNotes('');
      setStatus(
        payload.game.variants.length
          ? `${payload.game.title} loaded with ${payload.game.variants.length} variant(s).`
          : `${payload.game.title} loaded. Register a ROM variant next.`,
      );
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Unable to load game.');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!authenticated) return;
    void refreshGames();
    void refreshCatalogState();
  }, [authenticated, apiBase]);

  function persistToken() {
    const normalized = token.trim();
    if (!normalized) {
      sessionStorage.removeItem('retro-arcade-admin-token');
      setStatus('Admin token cleared.');
      return;
    }
    sessionStorage.setItem('retro-arcade-admin-token', normalized);
    setToken(normalized);
    setStatus('Admin token stored for this browser tab session only.');
    void refreshGames();
    void refreshCatalogState();
  }

  async function searchMetadata() {
    if (!query.trim()) return;
    setBusy(true);
    setStatus('Searching metadata provider…');
    try {
      const params = new URLSearchParams({ q: query.trim(), platform: form.platform });
      const payload = await api<{ results: MetadataCandidate[] }>(
        `/api/v1/admin/metadata/search?${params.toString()}`,
      );
      setMetadataResults(payload.results);
      setStatus(
        payload.results.length
          ? `Found ${payload.results.length} candidate(s).`
          : 'No matches; use manual entry.',
      );
    } catch (error) {
      setMetadataResults([]);
      setStatus(
        error instanceof Error
          ? `${error.message} Manual entry remains available.`
          : 'Metadata search failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  function applyCandidate(candidate: MetadataCandidate) {
    setForm((current) => ({
      ...current,
      title: candidate.title,
      platform: createdGame ? current.platform : (candidate.platform ?? current.platform),
      gameType: 'OFFICIAL',
      summary: candidate.summary ?? '',
      releaseDate: candidate.releaseDate ?? '',
      developer: candidate.developer ?? '',
      publisher: candidate.publisher ?? '',
      externalMetadataSource: candidate.provider,
      externalMetadataId: candidate.externalId,
    }));
    setStatus(`Metadata selected: ${candidate.title}. Review every field before saving.`);
  }

  async function createGame() {
    setBusy(true);
    setStatus('Creating private draft…');
    try {
      const payload = await api<{ game: GameRecord }>('/api/v1/admin/games', {
        method: 'POST',
        body: JSON.stringify({
          title: form.title,
          ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
          platform: form.platform,
          gameType: form.gameType,
          visibility: 'PRIVATE',
          summary: form.summary || null,
          releaseDate: form.releaseDate || null,
          developer: form.developer || null,
          publisher: form.publisher || null,
          externalMetadataSource: form.externalMetadataSource || null,
          externalMetadataId: form.externalMetadataId || null,
        }),
      });
      setCreatedGame({ ...payload.game, variants: [] });
      setStatus(`Draft created: ${payload.game.title}. Select the ROM file next.`);
      await refreshGames();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Draft creation failed.');
    } finally {
      setBusy(false);
    }
  }

  async function updateGameMetadata() {
    if (!createdGame) return;

    setBusy(true);
    setStatus('Saving catalog metadata…');
    try {
      const wasPublished = createdGame.status === 'PUBLISHED';
      const payload = await api<{ game: GameDetail }>(`/api/v1/admin/games/${createdGame.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          rowVersion: createdGame.rowVersion,
          title: form.title.trim(),
          slug: form.slug.trim(),
          gameType: form.gameType,
          summary: form.summary.trim() || null,
          releaseDate: form.releaseDate.trim() || null,
          developer: form.developer.trim() || null,
          publisher: form.publisher.trim() || null,
          externalMetadataSource: form.externalMetadataSource.trim() || null,
          externalMetadataId: form.externalMetadataId.trim() || null,
        }),
      });

      setCreatedGame(payload.game);
      setVariant(preferredVariant(payload.game.variants));
      setForm(formFromGame(payload.game));
      setStatus(
        wasPublished
          ? 'Metadata updated. Public catalog revision advanced so Player can refresh the published title.'
          : 'Metadata updated.',
      );
      await Promise.all([refreshGames(), refreshCatalogState()]);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Metadata update failed.');
    } finally {
      setBusy(false);
    }
  }

  async function inspectFile(selected: File | null) {
    const existingVariant = variant;

    setFile(selected);
    setInspection(null);
    setDuplicate(null);
    setUpload(null);
    setUploadProgress(0);
    setTestSession(null);
    setTestToken(null);
    if (!selected) return;

    setBusy(true);
    setStatus('Inspecting file locally and calculating SHA-256…');
    try {
      const result = await inspectRomFile(selected);
      setInspection(result);
      if (createdGame && result.platform !== createdGame.platform) {
        setStatus(
          `Platform mismatch: draft is ${createdGame.platform}, file appears to be ${result.platform}.`,
        );
        return;
      }

      if (
        existingVariant &&
        (existingVariant.status === 'DRAFT' || existingVariant.status === 'UPLOAD_FAILED')
      ) {
        const identityMatches =
          result.sha256 === existingVariant.sha256 &&
          result.sizeBytes === existingVariant.sizeBytes;
        setStatus(
          identityMatches
            ? 'Selected file matches the registered variant identity. Ready to upload.'
            : 'Selected file does not match the registered variant SHA-256/size. Choose the original binary.',
        );
        return;
      }

      const checked = await api<{ accepted: boolean; duplicate: VariantRecord | null }>(
        '/api/v1/admin/roms/inspect/check',
        { method: 'POST', body: JSON.stringify(inspectionPayload(result)) },
      );
      setDuplicate(checked.duplicate);
      setStatus(
        checked.duplicate
          ? `Duplicate detected: this SHA-256 already belongs to variant ${checked.duplicate.id}.`
          : 'Inspection accepted. The binary has not been uploaded yet.',
      );
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'ROM inspection failed.');
    } finally {
      setBusy(false);
    }
  }

  async function createVariant() {
    if (!createdGame || !inspection) return;
    setBusy(true);
    setStatus('Registering immutable ROM identity…');
    try {
      const payload = await api<{ variant: VariantRecord }>(
        `/api/v1/admin/games/${createdGame.id}/variants`,
        { method: 'POST', body: JSON.stringify(inspectionPayload(inspection)) },
      );
      setVariant(payload.variant);
      setStatus(`Variant ${payload.variant.id} created. Ready for direct R2 upload.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Variant creation failed.');
    } finally {
      setBusy(false);
    }
  }

  async function startUpload() {
    if (!variant || !file) return;
    setBusy(true);
    setUploadProgress(0);
    setStatus('Requesting short-lived R2 upload authorization…');
    try {
      const payload = await api<{ upload: UploadRecord; authorization: UploadAuthorization }>(
        '/api/v1/admin/uploads',
        { method: 'POST', body: JSON.stringify({ variantId: variant.id }) },
      );
      setUpload(payload.upload);
      const directUpload = uploadWithProgress(payload.authorization, file, setUploadProgress);
      uploadAbortRef.current = directUpload.abort;
      setStatus('Uploading directly from this browser to private R2…');
      await directUpload.promise;
      uploadAbortRef.current = null;
      setStatus('Upload reached R2. Verifying and finalizing…');
      const finalized = await api<{ variant: VariantRecord; upload: UploadRecord }>(
        `/api/v1/admin/uploads/${payload.upload.id}/finalize`,
        { method: 'POST' },
      );
      setUpload(finalized.upload);
      setVariant(finalized.variant);
      setUploadProgress(100);
      setStatus('Upload verified. Variant is UPLOADED and ready for private runtime validation.');
      await refreshGames();
    } catch (error) {
      uploadAbortRef.current = null;
      setStatus(error instanceof Error ? error.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  }

  async function abortUpload() {
    if (!upload) return;
    uploadAbortRef.current?.();
    setBusy(true);
    try {
      const payload = await api<{ upload: UploadRecord }>(
        `/api/v1/admin/uploads/${upload.id}/abort`,
        { method: 'POST' },
      );
      setUpload(payload.upload);
      setVariant((current) => (current ? { ...current, status: 'DRAFT' } : current));
      setUploadProgress(0);
      setStatus('Upload session aborted and any partial object was removed. You can retry.');
      setUpload(null);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Upload abort failed.');
    } finally {
      setBusy(false);
    }
  }

  async function createCompatibilitySession() {
    if (!variant || !createdGame) return;
    setBusy(true);
    setStatus('Creating private Player compatibility session…');
    try {
      const payload = await api<{ session: TestSessionRecord; token: string }>(
        `/api/v1/admin/variants/${variant.id}/test-session`,
        { method: 'POST' },
      );
      setTestSession(payload.session);
      setTestToken(payload.token);
      setVariant({ ...variant, status: 'TESTING' });
      setCreatedGame({ ...createdGame, status: 'TESTING' });
      setStatus(
        'Private test session created. Open Player, validate gameplay, then record PASS/FAIL.',
      );
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Unable to create test session.');
    } finally {
      setBusy(false);
    }
  }

  async function recordCompatibility(result: 'PASS' | 'FAIL') {
    if (!variant || !createdGame || !testSession) return;
    setBusy(true);
    setStatus(`Recording compatibility result: ${result}…`);
    try {
      await api(`/api/v1/admin/variants/${variant.id}/compatibility`, {
        method: 'POST',
        body: JSON.stringify({
          testSessionId: testSession.id,
          result,
          runtimeId: testSession.runtimeId,
          browser: navigator.userAgent.slice(0, 160),
          notes: compatibilityNotes.trim() || null,
        }),
      });
      setTestSession({ ...testSession, status: 'COMPLETED' });
      setTestToken(null);
      await refreshSelectedGame(
        createdGame.id,
        result === 'PASS'
          ? 'Compatibility PASS recorded. Content is READY for publication.'
          : 'Compatibility FAIL recorded. Variant returned to UPLOADED for another test.',
      );
      await refreshGames();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Compatibility result failed.');
    } finally {
      setBusy(false);
    }
  }

  async function publishGame() {
    if (!createdGame || !variant) return;
    setBusy(true);
    setStatus('Publishing validated variant…');
    try {
      const payload = await api<{ catalogState: CatalogState }>(
        `/api/v1/admin/games/${createdGame.id}/publish`,
        {
          method: 'POST',
          body: JSON.stringify({ variantId: variant.id }),
        },
      );
      setCatalogState(payload.catalogState);
      await refreshSelectedGame(
        createdGame.id,
        'Published. The game is now eligible for the public Player catalog.',
      );
      await refreshGames();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Publication failed.');
    } finally {
      setBusy(false);
    }
  }

  async function unpublishGame() {
    if (!createdGame) return;
    setBusy(true);
    setStatus('Removing game from the public catalog…');
    try {
      const payload = await api<{ catalogState: CatalogState }>(
        `/api/v1/admin/games/${createdGame.id}/unpublish`,
        { method: 'POST' },
      );
      setCatalogState(payload.catalogState);
      await refreshSelectedGame(
        createdGame.id,
        'Unpublished. Compatibility evidence remains, but public launch is disabled.',
      );
      await refreshGames();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Unpublish failed.');
    } finally {
      setBusy(false);
    }
  }

  function resetWizard() {
    uploadAbortRef.current?.();
    setForm(emptyForm());
    setCreatedGame(null);
    setMetadataResults([]);
    setQuery('');
    setFile(null);
    setInspection(null);
    setDuplicate(null);
    setVariant(null);
    setUpload(null);
    setUploadProgress(0);
    setTestSession(null);
    setTestToken(null);
    setCompatibilityNotes('');
    setStatus('New private ingestion draft ready.');
  }

  const testLaunchUrl =
    testSession && testToken
      ? `${playerBase()}/?testSession=${encodeURIComponent(testSession.id)}#token=${encodeURIComponent(testToken)}`
      : null;

  return (
    <main className="admin-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">{adminAppMeta.milestone} · Catalog management</p>
          <h1>{adminAppMeta.name}</h1>
        </div>
        <div className="topbar-actions">
          <span className="revision-chip">Catalog revision: {catalogState?.revision ?? '—'}</span>
          <button className="secondary" type="button" onClick={resetWizard} disabled={busy}>
            New game
          </button>
        </div>
      </header>

      <section className="connection-card" aria-label="Admin API connection">
        <label>
          API base URL
          <input
            value={apiBase}
            onChange={(event) => setApiBase(event.target.value.replace(/\/$/, ''))}
          />
        </label>
        <label>
          Owner bearer token
          <input
            type="password"
            autoComplete="off"
            value={token}
            onChange={(event) => setToken(event.target.value)}
            placeholder="Stored only in sessionStorage"
          />
        </label>
        <button type="button" onClick={persistToken}>
          Use for this session
        </button>
      </section>

      <div className="layout">
        <aside className="catalog-panel">
          <div className="section-heading">
            <h2>Owner catalog</h2>
            <button
              className="text-button"
              type="button"
              onClick={() => void refreshGames()}
              disabled={!authenticated}
            >
              Refresh
            </button>
          </div>
          <p className="muted">
            Select an existing record to continue its upload/test/publish flow.
          </p>
          {games.length === 0 ? <p className="muted">No records loaded.</p> : null}
          <ul className="game-list">
            {games.map((game) => (
              <li key={game.id}>
                <button
                  className="game-select"
                  type="button"
                  onClick={() => void selectExistingGame(game.id)}
                  disabled={busy}
                >
                  <strong>{game.title}</strong>
                  <span>
                    {game.platform.toUpperCase()} · {game.status}
                  </span>
                  <code>{game.id}</code>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="wizard" aria-labelledby="wizard-title">
          <div className="section-heading">
            <div>
              <p className="step-label">OWNER WORKFLOW</p>
              <h2 id="wizard-title">{createdGame ? createdGame.title : 'Add or select a game'}</h2>
            </div>
            <span className={busy ? 'badge active' : 'badge'}>{busy ? 'Working' : 'Ready'}</span>
          </div>

          <p className="status-box" role="status">
            {status}
          </p>

          <fieldset disabled={!authenticated || busy}>
            <legend>1. Metadata</legend>
            <div className="search-row">
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search a game title"
              />
              <button type="button" onClick={() => void searchMetadata()}>
                Search
              </button>
            </div>
            {metadataResults.length > 0 ? (
              <div className="candidate-grid">
                {metadataResults.map((candidate) => (
                  <button
                    className="candidate"
                    type="button"
                    key={`${candidate.provider}:${candidate.externalId}`}
                    onClick={() => applyCandidate(candidate)}
                  >
                    <strong>{candidate.title}</strong>
                    <span>{candidate.platform?.toUpperCase() ?? 'Unknown platform'}</span>
                    <small>{candidate.releaseDate ?? 'Unknown release'}</small>
                  </button>
                ))}
              </div>
            ) : null}

            <div className="form-grid">
              <label>
                Title
                <input
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                />
              </label>
              <label>
                Slug (optional)
                <input
                  value={form.slug}
                  onChange={(event) => setForm({ ...form, slug: event.target.value })}
                />
              </label>
              <label>
                Platform
                <select
                  value={form.platform}
                  disabled={Boolean(createdGame)}
                  onChange={(event) =>
                    setForm({ ...form, platform: event.target.value as Platform })
                  }
                >
                  <option value="gb">Game Boy</option>
                  <option value="gbc">Game Boy Color</option>
                  <option value="gba">Game Boy Advance</option>
                  <option value="nds">Nintendo DS</option>
                </select>
              </label>
              <label>
                Type
                <select
                  value={form.gameType}
                  onChange={(event) =>
                    setForm({ ...form, gameType: event.target.value as GameType })
                  }
                >
                  <option value="OFFICIAL">Official</option>
                  <option value="ROM_HACK">ROM hack</option>
                  <option value="HOMEBREW">Homebrew</option>
                  <option value="OTHER">Other</option>
                </select>
              </label>
              <label>
                Release date
                <input
                  value={form.releaseDate}
                  onChange={(event) => setForm({ ...form, releaseDate: event.target.value })}
                  placeholder="YYYY-MM-DD"
                />
              </label>
              <label>
                Developer
                <input
                  value={form.developer}
                  onChange={(event) => setForm({ ...form, developer: event.target.value })}
                />
              </label>
              <label>
                Publisher
                <input
                  value={form.publisher}
                  onChange={(event) => setForm({ ...form, publisher: event.target.value })}
                />
              </label>
              <label className="wide">
                Summary
                <textarea
                  rows={4}
                  value={form.summary}
                  onChange={(event) => setForm({ ...form, summary: event.target.value })}
                />
              </label>
            </div>
            {createdGame ? (
              <button
                type="button"
                onClick={() => void updateGameMetadata()}
                disabled={!canSaveMetadata}
              >
                Save metadata changes
              </button>
            ) : (
              <button type="button" onClick={() => void createGame()} disabled={!canCreateGame}>
                Create private draft
              </button>
            )}
          </fieldset>

          <fieldset
            disabled={
              !createdGame ||
              busy ||
              Boolean(variant && variant.status !== 'DRAFT' && variant.status !== 'UPLOAD_FAILED')
            }
          >
            <legend>2. Local ROM inspection</legend>
            <input
              type="file"
              accept=".gb,.gbc,.gba,.nds"
              onChange={(event) => void inspectFile(event.target.files?.[0] ?? null)}
            />
            {inspection ? (
              <dl className="inspection-grid">
                <div>
                  <dt>Platform</dt>
                  <dd>{inspection.platform.toUpperCase()}</dd>
                </div>
                <div>
                  <dt>Size</dt>
                  <dd>{(inspection.sizeBytes / 1024 / 1024).toFixed(2)} MiB</dd>
                </div>
                <div>
                  <dt>Internal title</dt>
                  <dd>{inspection.internalTitle ?? '—'}</dd>
                </div>
                <div>
                  <dt>Game code</dt>
                  <dd>{inspection.gameCode ?? '—'}</dd>
                </div>
                <div className="wide">
                  <dt>SHA-256</dt>
                  <dd>
                    <code>{inspection.sha256}</code>
                  </dd>
                </div>
              </dl>
            ) : null}
            {duplicate ? <p className="error-box">Duplicate variant: {duplicate.id}</p> : null}
            <button type="button" onClick={() => void createVariant()} disabled={!canCreateVariant}>
              Register ROM variant
            </button>
          </fieldset>

          <fieldset disabled={!variant}>
            <legend>3. Direct R2 upload</legend>
            <p className="muted">
              Existing uploaded variants can skip this step. New binaries go directly from this
              browser to private R2; the Worker receives metadata only.
            </p>
            <div className="progress-track" aria-label={`Upload ${uploadProgress}%`}>
              <span style={{ width: `${uploadProgress}%` }} />
            </div>
            <div className="actions">
              <button
                type="button"
                onClick={() => void startUpload()}
                disabled={!canUpload || busy}
              >
                Upload & verify
              </button>
              <button
                className="secondary"
                type="button"
                onClick={() => void abortUpload()}
                disabled={!upload || upload.status === 'COMPLETED'}
              >
                Abort upload
              </button>
            </div>
            <p className="muted">
              Variant status: <strong>{variant?.status ?? '—'}</strong>
            </p>
          </fieldset>

          <fieldset disabled={!variant || busy}>
            <legend>4. Private runtime validation</legend>
            <p className="muted">
              A PASS result is accepted only when it is bound to a short-lived owner test session
              for the exact uploaded variant and validated runtime.
            </p>
            <button
              type="button"
              onClick={() => void createCompatibilitySession()}
              disabled={!canCreateTestSession}
            >
              Create private Player test
            </button>

            {testSession ? (
              <div className="session-card">
                <div>
                  <span>Session</span>
                  <code>{testSession.id}</code>
                </div>
                <div>
                  <span>Runtime</span>
                  <code>{testSession.runtimeId}</code>
                </div>
                <div>
                  <span>Status</span>
                  <strong>{testSession.status}</strong>
                </div>
                <div>
                  <span>Expires</span>
                  <strong>{new Date(testSession.expiresAt).toLocaleTimeString()}</strong>
                </div>
              </div>
            ) : null}

            {testLaunchUrl ? (
              <a className="primary-link" href={testLaunchUrl} target="_blank" rel="noreferrer">
                Open private Player test
              </a>
            ) : null}

            <label>
              Compatibility notes (optional)
              <textarea
                rows={3}
                value={compatibilityNotes}
                onChange={(event) => setCompatibilityNotes(event.target.value)}
                placeholder="Boot, controls, rendering, audio, browser observations…"
              />
            </label>

            <div className="actions">
              <button
                type="button"
                onClick={() => void recordCompatibility('PASS')}
                disabled={!canRecordCompatibility}
              >
                Record PASS
              </button>
              <button
                className="danger"
                type="button"
                onClick={() => void recordCompatibility('FAIL')}
                disabled={!canRecordCompatibility}
              >
                Record FAIL
              </button>
            </div>
          </fieldset>

          <fieldset disabled={!createdGame || !variant || busy}>
            <legend>5. Publication</legend>
            <div className="publication-state">
              <span>
                Game: <strong>{createdGame?.status ?? '—'}</strong>
              </span>
              <span>
                Variant: <strong>{variant?.status ?? '—'}</strong>
              </span>
              <span>
                Catalog revision: <strong>{catalogState?.revision ?? '—'}</strong>
              </span>
            </div>
            <p className="warning-box">
              Publish only content you are authorized to distribute. Publication makes the selected
              validated variant launchable from the public Player catalog.
            </p>
            <div className="actions">
              <button type="button" onClick={() => void publishGame()} disabled={!canPublish}>
                Publish validated variant
              </button>
              <button
                className="secondary"
                type="button"
                onClick={() => void unpublishGame()}
                disabled={!canUnpublish}
              >
                Unpublish
              </button>
            </div>
          </fieldset>
        </section>
      </div>
    </main>
  );
}
